import { find } from "lodash";
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import Input from "antd/lib/input";
import Select from "antd/lib/select";
import { Query } from "@/services/query";
import PlainButton from "@/components/plain-button/PlainButton";
import notification from "@/services/notification";
import { QueryTagsControl } from "@/components/tags-control/TagsControl";
import useSearchResults from "@/lib/hooks/useSearchResults";
import { useTheme } from "@/components/ThemeProvider";

import "./QuerySelector.less";

const { Option } = Select;

function search(term) {
  if (term === null) {
    return Promise.resolve(null);
  }

  // get recent
  if (!term) {
    return Query.recent().then(results => results.filter(item => !item.is_draft)); // filter out draft
  }

  // search by query
  return Query.query({ q: term }).then(({ results }) => results);
}

export default function QuerySelector(props) {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedQuery, setSelectedQuery] = useState();
  const [doSearch, searchResults, searching] = useSearchResults(search, { initialResults: [] });
  const { isDarkMode } = useTheme();

  const placeholder = "Search a query by name";
  const clearIcon = (
    <i
      className="fa fa-times hide-in-percy"
      role="button"
      tabIndex={0}
      aria-label="Clear"
      onClick={(e) => {
        e.stopPropagation(); // Prevent event from bubbling up
        selectQuery(null);
      }}
      style={{ cursor: 'pointer' }} // Make it visually clear it's clickable
    />
  );
  const spinIcon = (
    <span role="status" aria-live="polite" aria-relevant="additions removals">
      <i className={cx("fa fa-spinner fa-pulse hide-in-percy", { hidden: !searching })} aria-hidden="true" />
      <span className="sr-only">Searching...</span>
    </span>
  );

  useEffect(() => {
    doSearch(searchTerm);
  }, [doSearch, searchTerm]);

  // set selected from prop
  useEffect(() => {
    if (props.selectedQuery) {
      setSelectedQuery(props.selectedQuery);
    }
  }, [props.selectedQuery]);

  function selectQuery(queryId) {
    let query = null;
    if (queryId) {
      query = find(searchResults, { id: queryId });
      if (!query) {
        // shouldn't happen
        notification.error("Something went wrong...", "Couldn't select query");
      }
    }

    setSearchTerm(query ? null : ""); // empty string triggers recent fetch
    setSelectedQuery(query);
    props.onChange(query);
  }

  function renderResults() {
    if (!searchResults.length) {
      return <div className="text-muted">No results matching search term.</div>;
    }

    return (
      <ul className="list-group">
        {searchResults.map(q => (
          <PlainButton
            className={cx("query-selector-result", "list-group-item", { inactive: q.is_draft })}
            key={q.id}
            role="listitem"
            onClick={() => selectQuery(q.id)}
            data-test={`QueryId${q.id}`}>
            {q.name} <QueryTagsControl isDraft={q.is_draft} tags={q.tags} className="inline-tags-control" />
          </PlainButton>
        ))}
      </ul>
    );
  }

  if (props.disabled) {
    return (
      <Input
        value={selectedQuery && selectedQuery.name}
        aria-label="Tied query"
        placeholder={placeholder}
        disabled
        className={cx("query-selector", { "query-selector-dark": isDarkMode })}
      />
    );
  }

  if (props.type === "select") {
    const suffixIcon = selectedQuery ? clearIcon : null;
    const value = selectedQuery ? selectedQuery.name : searchTerm;

    return (
      <Select
        showSearch
        dropdownMatchSelectWidth={false}
        placeholder={placeholder}
        value={value || undefined} // undefined for the placeholder to show
        onSearch={setSearchTerm}
        onChange={selectQuery}
        suffixIcon={searching ? spinIcon : suffixIcon}
        allowClear={!!selectedQuery} // Add this prop to enable built-in clear functionality
        onClear={() => selectQuery(null)} // Handle the clear event
        notFoundContent={null}
        filterOption={false}
        defaultActiveFirstOption={false}
        className={cx(props.className, "query-selector", { "query-selector-dark": isDarkMode })}
        dropdownClassName={cx({ "ant-select-dropdown-dark": isDarkMode })}
        data-test="QuerySelector">
        {searchResults &&
          searchResults.map(q => {
            const disabled = q.is_draft;
            return (
              <Option
                value={q.id}
                key={q.id}
                disabled={disabled}
                className="query-selector-result"
                data-test={`QueryId${q.id}`}>
                {q.name}{" "}
                <QueryTagsControl
                  isDraft={q.is_draft}
                  tags={q.tags}
                  className={cx("inline-tags-control", { disabled, "tags-dark": isDarkMode })}
                />
              </Option>
            );
          })}
      </Select>
    );
  }

  return (
    <div className={cx("query-selector-container", { "dark-mode": isDarkMode })}>
      <span data-test="QuerySelector" className="query-selector">
        {selectedQuery ? (
          <Input
            value={selectedQuery.name}
            aria-label="Tied query"
            suffix={clearIcon}
            readOnly
            allowClear={{
              clearIcon: <span onClick={() => selectQuery(null)}>×</span>
            }}
          />
        ) : (
          <Input
            placeholder={placeholder}
            value={searchTerm}
            aria-label="Tied query"
            onChange={e => setSearchTerm(e.target.value)}
            suffix={spinIcon}
          />
        )}
        <div className="scrollbox" style={{ maxHeight: "50vh", marginTop: 15 }}>
          {searchResults && renderResults()}
        </div>
      </span>
    </div>
  );
}

QuerySelector.propTypes = {
  onChange: PropTypes.func.isRequired,
  selectedQuery: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  type: PropTypes.oneOf(["select", "default"]),
  className: PropTypes.string,
  disabled: PropTypes.bool,
};

QuerySelector.defaultProps = {
  selectedQuery: null,
  type: "default",
  className: null,
  disabled: false,
};
